import type { ReviewClassification, TerritoryInfo } from "../../lib/review-api";
import { cn } from "../cn";

/** NEW / REPUBLISH / REVIEWED classification pill. Coral is reserved for
 *  moderation, so REPUBLISH uses amber and NEW/REVIEWED use the cool palette. */
const CLASS_STYLE: Record<ReviewClassification, { label: string; style: React.CSSProperties }> = {
  NEW: { label: "New", style: { color: "var(--spec-cyan)", borderColor: "var(--spec-cyan)" } },
  REPUBLISH: { label: "Republish", style: { color: "#f0b429", borderColor: "rgba(240,180,41,0.5)" } },
  REVIEWED: { label: "Reviewed", style: { color: "var(--state-success, #58d68d)", borderColor: "rgba(88,214,141,0.45)" } },
  UNPUBLISHED: { label: "Unpublished", style: { color: "var(--text-muted)", borderColor: "var(--border-soft, rgba(255,255,255,0.14))" } },
};

export function ReviewBadge({ classification, testid }: { classification: ReviewClassification; testid?: string }) {
  const s = CLASS_STYLE[classification] ?? CLASS_STYLE.NEW;
  return (
    <span
      data-testid={testid}
      data-classification={classification}
      className="inline-flex items-center h-5 px-2 rounded-sm border font-mono text-[8.5px] font-semibold uppercase tracking-[0.14em] whitespace-nowrap"
      style={s.style}
    >
      {s.label}
    </span>
  );
}

export type OwnerState = "LIVE_PENDING" | "LIVE_REVIEWED" | "HIDDEN_MODERATION" | "UNPUBLISHED";

export function deriveOwnerState(info: Pick<TerritoryInfo, "review" | "has_active_publication" | "moderation">): OwnerState {
  if (info.moderation?.moderated) return "HIDDEN_MODERATION";
  if (!info.has_active_publication) return "UNPUBLISHED";
  if (info.review?.reviewed) return "LIVE_REVIEWED";
  return "LIVE_PENDING";
}

const OWNER_STATE_STYLE: Record<OwnerState, { label: string; style: React.CSSProperties }> = {
  LIVE_PENDING: { label: "Live · Review Pending", style: { color: "#f0b429", borderColor: "rgba(240,180,41,0.5)" } },
  LIVE_REVIEWED: { label: "Live · Reviewed", style: { color: "var(--state-success, #58d68d)", borderColor: "rgba(88,214,141,0.45)" } },
  HIDDEN_MODERATION: { label: "Hidden · Moderation Active", style: { color: "var(--neon-coral)", borderColor: "var(--neon-coral-ring)" } },
  UNPUBLISHED: { label: "Unpublished", style: { color: "var(--text-muted)", borderColor: "var(--border-soft, rgba(255,255,255,0.14))" } },
};

export function OwnerStateBadge({ state, testid }: { state: OwnerState; testid?: string }) {
  const s = OWNER_STATE_STYLE[state];
  return (
    <span
      data-testid={testid}
      data-owner-state={state}
      className={cn(
        "inline-flex items-center h-5 px-2 rounded-sm border font-mono text-[8.5px] font-semibold uppercase tracking-[0.14em] whitespace-nowrap",
      )}
      style={s.style}
    >
      {s.label}
    </span>
  );
}
